import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  Chip,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Pagination
} from '@mui/material';
import {
  Check,
  Close,
  Refresh,
  AutoAwesome,
  Search,
  Link as LinkIcon,
  Person,
  Business,
  Email,
  Phone,
  Domain,
  LocationOn
} from '@mui/icons-material';
import axios from 'axios';
import ConfirmDialog from '../components/ConfirmDialog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Contact Card Component
const ContactCard = ({ contact, matchDetails }) => {
  const highlightFields = matchDetails ? JSON.parse(matchDetails) : {};
  const isEmailMatch = highlightFields.matched_field === 'email_domain' ||
    highlightFields.signals?.some(s => s.type === 'email_domain');
  const isNameMatch = highlightFields.matched_field?.includes('account_name') ||
    highlightFields.signals?.some(s => s.type === 'fuzzy_name');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: 'grey.100',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2.5,
        height: '100%',
        transition: 'border-color 0.3s ease-out'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Person sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {contact?.name || 'Unknown'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          p: 0.75, borderRadius: 1.5,
          bgcolor: isEmailMatch ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
          border: isEmailMatch ? '1px solid rgba(46, 125, 50, 0.3)' : '1px solid transparent'
        }}>
          <Email sx={{ fontSize: 16, color: isEmailMatch ? 'success.main' : 'grey.400' }} />
          <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.8rem' }}>
            {contact?.email || 'No email'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.75 }}>
          <Phone sx={{ fontSize: 16, color: 'grey.400' }} />
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            {contact?.phone || 'No phone'}
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          p: 0.75, borderRadius: 1.5,
          bgcolor: isNameMatch ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
          border: isNameMatch ? '1px solid rgba(46, 125, 50, 0.3)' : '1px solid transparent'
        }}>
          <Business sx={{ fontSize: 16, color: isNameMatch ? 'success.main' : 'grey.400' }} />
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {contact?.account_name || 'No account'}
          </Typography>
        </Box>

        <Box sx={{ px: 0.75 }}>
          <Chip
            label={contact?.site_id ? 'Linked' : 'Not linked'}
            size="small"
            sx={{
              height: 20, fontSize: '0.65rem', fontWeight: 600,
              bgcolor: contact?.site_id ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0,0,0,0.06)',
              color: contact?.site_id ? 'success.dark' : 'text.secondary'
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

// Site Card Component
const SiteCard = ({ site, matchDetails }) => {
  const highlightFields = matchDetails ? JSON.parse(matchDetails) : {};
  const isEmailMatch = highlightFields.matched_field === 'email_domain' ||
    highlightFields.signals?.some(s => s.type === 'email_domain');
  const isNameMatch = highlightFields.matched_field?.includes('account_name') ||
    highlightFields.signals?.some(s => s.type === 'fuzzy_name');

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: 'grey.100',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2.5,
        height: '100%',
        transition: 'border-color 0.3s ease-out'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Business sx={{ color: 'secondary.dark', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
          {site?.account_name || site?.rootdomain || 'Unknown'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          p: 0.75, borderRadius: 1.5,
          bgcolor: isEmailMatch ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
          border: isEmailMatch ? '1px solid rgba(46, 125, 50, 0.3)' : '1px solid transparent'
        }}>
          <Domain sx={{ fontSize: 16, color: isEmailMatch ? 'success.main' : 'grey.400' }} />
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {site?.rootdomain || 'No domain'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.75 }}>
          <Chip
            label={site?.account_status || 'Unknown'}
            size="small"
            sx={{
              height: 22, fontSize: '0.7rem', fontWeight: 600,
              ...(site?.account_status === 'Active'
                ? { bgcolor: 'rgba(46, 125, 50, 0.1)', color: 'success.dark' }
                : {})
            }}
            color={site?.account_status === 'Active' ? undefined : 'default'}
          />
        </Box>

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          p: 0.75, borderRadius: 1.5,
          bgcolor: isNameMatch ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
          border: isNameMatch ? '1px solid rgba(46, 125, 50, 0.3)' : '1px solid transparent'
        }}>
          <Business sx={{ fontSize: 16, color: isNameMatch ? 'success.main' : 'grey.400' }} />
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {site?.account_name || 'No account name'}
          </Typography>
        </Box>

        {(site?.billing_city || site?.billing_state) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.75 }}>
            <LocationOn sx={{ fontSize: 16, color: 'grey.400' }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              {[site?.billing_city, site?.billing_state].filter(Boolean).join(', ')}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// Suggestion Row Component
const SuggestionRow = ({ suggestion, selected, onSelect, onApprove, onReject }) => {
  const getMatchTypeColor = (type) => {
    switch (type) {
      case 'email_domain': return 'success';
      case 'exact_name': return 'primary';
      case 'fuzzy_name': return 'warning';
      case 'combined': return 'info';
      default: return 'default';
    }
  };

  const getMatchTypeLabel = (type) => {
    switch (type) {
      case 'email_domain': return 'Email Domain';
      case 'exact_name': return 'Exact Name';
      case 'fuzzy_name': return 'Fuzzy Name';
      case 'combined': return 'Combined';
      default: return type;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'grey.200',
        borderRadius: 3,
        boxShadow: selected
          ? '0px 4px 16px -4px rgba(23, 75, 106, 0.15)'
          : '0px 4px 12px -4px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease-out',
        '&:hover': {
          borderColor: selected ? 'primary.main' : 'grey.300',
          boxShadow: '0px 8px 20px -6px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Checkbox */}
        <Checkbox
          checked={selected}
          onChange={(e) => onSelect(suggestion.id, e.target.checked)}
          sx={{ mt: 1, color: 'grey.400' }}
        />

        {/* Contact Card */}
        <Box sx={{ flex: 1 }}>
          <ContactCard
            contact={suggestion.contact}
            matchDetails={suggestion.match_details}
          />
        </Box>

        {/* Match Info */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 120,
          py: 2,
          px: 1.5,
          borderRadius: 2,
          bgcolor: 'grey.100'
        }}>
          <LinkIcon sx={{ color: 'grey.400', fontSize: 20 }} />
          <Chip
            label={getMatchTypeLabel(suggestion.match_type)}
            color={getMatchTypeColor(suggestion.match_type)}
            size="small"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
          <Typography variant="h5" sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 600, color: 'primary.main'
          }}>
            {suggestion.confidence_score}%
          </Typography>
          <Typography variant="caption" sx={{
            color: 'text.secondary', fontWeight: 500,
            letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.6rem'
          }}>
            Confidence
          </Typography>
        </Box>

        {/* Site Card */}
        <Box sx={{ flex: 1 }}>
          <SiteCard
            site={suggestion.site}
            matchDetails={suggestion.match_details}
          />
        </Box>

        {/* Actions */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minWidth: 110
        }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<Check />}
            onClick={() => onApprove(suggestion.id)}
            fullWidth
            sx={{ borderRadius: 2, fontWeight: 600, py: 0.8 }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<Close />}
            onClick={() => onReject(suggestion.id)}
            fullWidth
            sx={{ borderRadius: 2, fontWeight: 600, py: 0.8 }}
          >
            Reject
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

// Manual Link Dialog
const ManualLinkDialog = ({ open, contactId, contactName, onClose, onLink }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (searchTerm.length < 2) return;

    setSearching(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_URL}/contact-link-suggestions/contacts/${contactId}/search-sites`,
        {
          params: { search: searchTerm },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = (siteId) => {
    onLink(contactId, siteId);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 2,
          bgcolor: 'info.main', display: 'flex',
          alignItems: 'center', justifyContent: 'center'
        }}>
          <LinkIcon sx={{ fontSize: 20, color: 'white' }} />
        </Box>
        <Box>
          <Typography sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 500, fontSize: '1.3rem', lineHeight: 1.2
          }}>
            Manual Link
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {contactName}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search sites by name or domain..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'grey.100', borderRadius: 2,
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'grey.300' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searching || searchTerm.length < 2}
            sx={{
              minWidth: 48, borderRadius: 2,
              bgcolor: 'primary.main', boxShadow: 'none',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            {searching ? <CircularProgress size={18} sx={{ color: 'white' }} /> : <Search />}
          </Button>
        </Box>

        <List sx={{
          maxHeight: 300, overflow: 'auto',
          bgcolor: 'grey.100', borderRadius: 2,
          border: '1px solid', borderColor: 'grey.200'
        }}>
          {searchResults.map((site) => (
            <ListItem key={site.id} disablePadding>
              <ListItemButton
                onClick={() => handleSelect(site.id)}
                sx={{
                  py: 1.5, px: 2,
                  borderBottom: '1px solid', borderColor: 'grey.200',
                  transition: 'background-color 0.2s ease',
                  '&:hover': { bgcolor: 'rgba(23, 75, 106, 0.06)' }
                }}
              >
                <ListItemText
                  primary={site.account_name || site.rootdomain}
                  secondary={
                    <>
                      {site.rootdomain}
                      {site.billing_city && ` - ${site.billing_city}, ${site.billing_state}`}
                    </>
                  }
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                  secondaryTypographyProps={{ fontSize: '0.78rem' }}
                />
                <Chip
                  label={site.account_status || 'Unknown'}
                  size="small"
                  sx={{
                    fontWeight: 600, fontSize: '0.7rem',
                    ...(site.account_status === 'Active'
                      ? { bgcolor: 'rgba(46, 125, 50, 0.1)', color: 'success.dark' }
                      : {})
                  }}
                  color={site.account_status === 'Active' ? undefined : 'default'}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {searchResults.length === 0 && searchTerm.length >= 2 && !searching && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Search sx={{ fontSize: 32, color: 'grey.300', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No sites found
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: 'text.secondary', fontWeight: 500, px: 2.5,
            '&:hover': { bgcolor: 'grey.100' }
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Component
const ContactLinkReview = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [matchType, setMatchType] = useState('');
  const [minConfidence, setMinConfidence] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Auto-approve dialog
  const [autoApproveOpen, setAutoApproveOpen] = useState(false);
  const [autoApproveThreshold, setAutoApproveThreshold] = useState(90);

  // Confirmation dialog
  const [confirmAction, setConfirmAction] = useState({ open: false, type: '', id: null, count: 0 });

  // Manual link dialog
  const [manualLinkOpen, setManualLinkOpen] = useState(false);
  const [manualLinkContact, setManualLinkContact] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/contact-link-suggestions/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = {
        status: 'pending',
        page,
        limit: 20
      };
      if (matchType) params.match_type = matchType;
      if (minConfidence > 0) params.min_confidence = minConfidence;

      const response = await axios.get(`${API_URL}/contact-link-suggestions/`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuggestions(response.data.suggestions);
      setTotalCount(response.data.total_count);
      setTotalPages(Math.ceil(response.data.total_count / 20));
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSnackbar({ open: true, message: 'Failed to load suggestions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, matchType, minConfidence]);

  useEffect(() => {
    fetchStats();
    fetchSuggestions();
  }, [fetchStats, fetchSuggestions]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/contact-link-suggestions/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        message: `Generated ${response.data.total} new suggestions`,
        severity: 'success'
      });
      fetchStats();
      fetchSuggestions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to generate suggestions', severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (suggestionId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/contact-link-suggestions/${suggestionId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: 'Contact linked successfully', severity: 'success' });
      fetchStats();
      fetchSuggestions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to approve', severity: 'error' });
    }
  };

  const handleReject = async (suggestionId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/contact-link-suggestions/${suggestionId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Find the suggestion to get contact info for manual link option
      const suggestion = suggestions.find(s => s.id === suggestionId);
      if (suggestion) {
        setManualLinkContact({
          id: suggestion.contact_id,
          name: suggestion.contact?.name
        });
        setManualLinkOpen(true);
      }

      fetchStats();
      fetchSuggestions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to reject', severity: 'error' });
    }
  };

  const handleSelect = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(suggestions.map(s => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/contact-link-suggestions/bulk-approve`,
        { suggestion_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        message: `Approved ${response.data.success_count} suggestions`,
        severity: 'success'
      });
      setSelectedIds([]);
      fetchStats();
      fetchSuggestions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Bulk approve failed', severity: 'error' });
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/contact-link-suggestions/bulk-reject`,
        { suggestion_ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        message: `Rejected ${response.data.success_count} suggestions`,
        severity: 'success'
      });
      setSelectedIds([]);
      fetchStats();
      fetchSuggestions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Bulk reject failed', severity: 'error' });
    }
  };

  const requestConfirm = (type, id = null) => {
    const count = type.startsWith('bulk') ? selectedIds.length : 1;
    setConfirmAction({ open: true, type, id, count });
  };

  const executeConfirmedAction = async () => {
    const { type, id } = confirmAction;
    setConfirmAction({ open: false, type: '', id: null, count: 0 });

    switch (type) {
      case 'approve':
        await handleApprove(id);
        break;
      case 'reject':
        await handleReject(id);
        break;
      case 'bulkApprove':
        await handleBulkApprove();
        break;
      case 'bulkReject':
        await handleBulkReject();
        break;
      default:
        break;
    }
  };

  const getConfirmMessage = () => {
    const { type, count } = confirmAction;
    switch (type) {
      case 'approve':
        return 'Are you sure you want to approve this link?';
      case 'reject':
        return 'Are you sure you want to reject this link?';
      case 'bulkApprove':
        return `Are you sure you want to approve these ${count} selected links?`;
      case 'bulkReject':
        return `Are you sure you want to reject these ${count} selected links?`;
      default:
        return '';
    }
  };

  const handleAutoApprove = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/contact-link-suggestions/auto-approve`,
        { min_confidence: autoApproveThreshold },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({
        open: true,
        message: `Auto-approved ${response.data.approved} of ${response.data.total_eligible} eligible suggestions`,
        severity: 'success'
      });
      setAutoApproveOpen(false);
      fetchStats();
      fetchSuggestions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Auto-approve failed', severity: 'error' });
    }
  };

  const handleManualLink = async (contactId, siteId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/contact-link-suggestions/contacts/${contactId}/manual-link`,
        { site_id: siteId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSnackbar({ open: true, message: 'Contact manually linked', severity: 'success' });
      fetchStats();
      fetchSuggestions();
    } catch (error) {
      setSnackbar({ open: true, message: 'Manual link failed', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            bgcolor: 'primary.main', display: 'flex',
            alignItems: 'center', justifyContent: 'center', mr: 2,
            boxShadow: '0 4px 12px rgba(23, 75, 106, 0.25)'
          }}>
            <LinkIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 500, letterSpacing: '-0.01em'
            }}>
              Contact Link Review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Review and approve suggested contact-to-site links
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={generating ? <CircularProgress size={18} /> : <Refresh />}
            onClick={handleGenerate}
            disabled={generating}
            sx={{
              borderColor: 'grey.300', color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(23, 75, 106, 0.04)' }
            }}
          >
            Generate Suggestions
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesome />}
            onClick={() => setAutoApproveOpen(true)}
            sx={{
              bgcolor: 'primary.main', px: 3,
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            Auto-Approve
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[
            { label: 'Pending', value: stats.pending, color: '#c6a862', bgColor: 'rgba(198, 168, 98, 0.08)' },
            { label: 'Approved', value: stats.approved, color: '#2e7d32', bgColor: 'rgba(46, 125, 50, 0.06)' },
            { label: 'Rejected', value: stats.rejected, color: '#d32f2f', bgColor: 'rgba(211, 47, 47, 0.06)' },
            { label: 'Unlinked', value: stats.unlinked_contacts, color: '#174b6a', bgColor: 'rgba(23, 75, 106, 0.06)' }
          ].map((stat) => (
            <Paper key={stat.label} sx={{
              p: 2.5, flex: 1, textAlign: 'center',
              borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
              bgcolor: stat.bgColor,
              boxShadow: '0px 4px 12px -4px rgba(0, 0, 0, 0.06)',
              transition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 8px 20px -6px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <Typography variant="h3" sx={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 600, color: stat.color, mb: 0.5
              }}>
                {stat.value}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary', fontWeight: 500,
                letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem'
              }}>
                {stat.label}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{
        p: 2.5, mb: 3, borderRadius: 3,
        border: '1px solid', borderColor: 'grey.200',
        boxShadow: '0px 4px 12px -4px rgba(0, 0, 0, 0.06)'
      }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Match Type</InputLabel>
            <Select
              value={matchType}
              label="Match Type"
              onChange={(e) => { setMatchType(e.target.value); setPage(1); }}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="email_domain">Email Domain</MenuItem>
              <MenuItem value="exact_name">Exact Name</MenuItem>
              <MenuItem value="fuzzy_name">Fuzzy Name</MenuItem>
              <MenuItem value="combined">Combined</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ width: 220 }}>
            <Typography variant="caption" sx={{
              color: 'text.secondary', fontWeight: 500,
              letterSpacing: '0.03em', textTransform: 'uppercase', fontSize: '0.7rem'
            }}>
              Min Confidence: {minConfidence}%
            </Typography>
            <Slider
              value={minConfidence}
              onChange={(e, v) => { setMinConfidence(v); setPage(1); }}
              min={0}
              max={100}
              size="small"
              sx={{
                color: 'primary.main',
                '& .MuiSlider-thumb': { width: 16, height: 16 }
              }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {selectedIds.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={`${selectedIds.length} selected`}
                size="small"
                sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white' }}
              />
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => requestConfirm('bulkApprove')}
                sx={{ borderRadius: 2 }}
              >
                Approve Selected
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => requestConfirm('bulkReject')}
                sx={{ borderRadius: 2 }}
              >
                Reject Selected
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Select All */}
      {suggestions.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Checkbox
            checked={selectedIds.length === suggestions.length && suggestions.length > 0}
            indeterminate={selectedIds.length > 0 && selectedIds.length < suggestions.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
            sx={{ color: 'grey.400' }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Select All
          </Typography>
          <Typography variant="body2" sx={{
            color: 'text.secondary', ml: 2,
            bgcolor: 'grey.100', px: 1.5, py: 0.25, borderRadius: 1, fontSize: '0.8rem'
          }}>
            Showing {suggestions.length} of {totalCount}
          </Typography>
        </Box>
      )}

      {/* Suggestions List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={36} sx={{ color: 'primary.main' }} />
        </Box>
      ) : suggestions.length === 0 ? (
        <Paper sx={{
          p: 6, textAlign: 'center', borderRadius: 3,
          border: '1px solid', borderColor: 'grey.200',
          boxShadow: '0px 4px 12px -4px rgba(0, 0, 0, 0.06)'
        }}>
          <LinkIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}>
            No pending suggestions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Generate Suggestions" to find matches.
          </Typography>
        </Paper>
      ) : (
        <>
          {suggestions.map((suggestion) => (
            <SuggestionRow
              key={suggestion.id}
              suggestion={suggestion}
              selected={selectedIds.includes(suggestion.id)}
              onSelect={handleSelect}
              onApprove={(id) => requestConfirm('approve', id)}
              onReject={(id) => requestConfirm('reject', id)}
            />
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Auto-Approve Dialog */}
      <Dialog open={autoApproveOpen} onClose={() => setAutoApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            bgcolor: 'primary.main', display: 'flex',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <AutoAwesome sx={{ fontSize: 20, color: 'white' }} />
          </Box>
          Auto-Approve Suggestions
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}>
            Automatically approve all pending suggestions with confidence score at or above:
          </Typography>
          <Box sx={{
            px: 3, py: 4, mx: 1,
            bgcolor: 'grey.100', borderRadius: 3,
            border: '1px solid', borderColor: 'grey.200',
            textAlign: 'center'
          }}>
            <Typography sx={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 600, color: 'primary.main',
              fontSize: '3rem', lineHeight: 1, mb: 2
            }}>
              {autoApproveThreshold}%
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={autoApproveThreshold}
                onChange={(e, v) => setAutoApproveThreshold(v)}
                min={50}
                max={100}
                marks={[
                  { value: 50, label: '50%' },
                  { value: 70, label: '70%' },
                  { value: 90, label: '90%' },
                  { value: 100, label: '100%' }
                ]}
                sx={{
                  color: 'primary.main',
                  '& .MuiSlider-thumb': { width: 20, height: 20 },
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.75rem', color: 'text.secondary'
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAutoApproveOpen(false)}
            sx={{
              color: 'text.secondary', fontWeight: 500, px: 2.5,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAutoApprove}
            startIcon={<AutoAwesome />}
            sx={{
              px: 3, borderRadius: 2,
              bgcolor: 'primary.main', boxShadow: 'none',
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: '0 4px 12px rgba(23, 75, 106, 0.25)'
              }
            }}
          >
            Auto-Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Link Dialog */}
      <ManualLinkDialog
        open={manualLinkOpen}
        contactId={manualLinkContact?.id}
        contactName={manualLinkContact?.name}
        onClose={() => {
          setManualLinkOpen(false);
          setManualLinkContact(null);
        }}
        onLink={handleManualLink}
      />

      <ConfirmDialog
        open={confirmAction.open}
        title={confirmAction.type.includes('pprove') ? 'Confirm Approval' : 'Confirm Rejection'}
        message={getConfirmMessage()}
        onConfirm={executeConfirmedAction}
        onCancel={() => setConfirmAction({ open: false, type: '', id: null, count: 0 })}
        confirmLabel="Yes"
        cancelLabel="No"
        confirmColor={confirmAction.type.includes('pprove') ? 'success' : 'error'}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactLinkReview;
